/**
 * Shared loader for the report account/group picker options (`/codenames`).
 *
 * Fetched ONCE and memoized so:
 *  - the backend's market-value aggregate is warmed at login (preload), not
 *    lazily on the first report popup, and
 *  - the ~15 report popups share one payload instead of each re-fetching.
 *
 * Cleared on logout so a new user never sees the previous user's accounts.
 */
import { API_ENDPOINTS } from '../../../config/api';
import { STORAGE_KEYS } from '../../../config/constants';
import { setLatestDataDate, setMaxSelectableDate } from './reportDates';

// The login response bundles the /codenames payload and SignInPage stashes it in
// sessionStorage just before the post-login hard reload. Seed the cache from it
// once, on module load, so the Portfolios list + report pickers render with zero
// fetch. One-shot: the key is removed on read so it never outlives the session
// that minted it (and a later user on the same tab cannot read it). A missing or
// malformed payload simply yields no seed and the normal fetch path runs.
function seedFromSession() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEYS.CODENAMES_PRELOAD);
    if (!raw) return null;
    sessionStorage.removeItem(STORAGE_KEYS.CODENAMES_PRELOAD);
    const parsed = JSON.parse(raw);
    // Persist the authoritative "latest data date" and the picker ceiling
    // durably (the preload key is one-shot/removed) so report date defaults and
    // the date inputs' max can read them.
    setLatestDataDate(parsed && parsed.latest_data_date);
    setMaxSelectableDate(parsed && parsed.max_selectable_date);
    return parsed;
  } catch (_) {
    return null;
  }
}

// Stash the login-bundled /codenames payload so the next module load (after the
// post-login hard reload) seeds the cache from it via seedFromSession. SignInPage
// calls this just before its hard reload; pairing the write with the read keeps
// the storage key owned by a single module.
export function stashCodenamesPreload(codenames) {
  if (!codenames) return;
  try {
    sessionStorage.setItem(STORAGE_KEYS.CODENAMES_PRELOAD, JSON.stringify(codenames));
  } catch (_) {
    // sessionStorage full/unavailable - the list falls back to a fetch.
  }
}

let cache = seedFromSession(); // resolved /codenames payload (login-preloaded if present)
let inflight = null; // dedupes concurrent callers

function authHeader() {
  const token =
    localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Resolve the picker options, reusing the cache (or an in-flight request).
 * Pass `{ force: true }` to bypass the cache and refetch.
 */
export function loadPortfolioOptions({ force = false } = {}) {
  if (!force && cache) return Promise.resolve(cache);
  if (!force && inflight) return inflight;
  inflight = fetch(API_ENDPOINTS.reports.getCodenames, { headers: authHeader() })
    .then((res) => {
      if (!res.ok) throw new Error(`codenames ${res.status}`);
      return res.json();
    })
    .then((data) => {
      cache = data;
      inflight = null;
      setLatestDataDate(data && data.latest_data_date);
      setMaxSelectableDate(data && data.max_selectable_date);
      return data;
    })
    .catch((err) => {
      inflight = null;
      throw err;
    });
  return inflight;
}

/** Warm the cache at login (fire-and-forget; errors are swallowed). */
export function preloadPortfolioOptions() {
  loadPortfolioOptions().catch(() => {});
}

let volumeCache = null; // resolved { account_number: volume } map (session-stable)
let volumeInflight = null;

/**
 * Resolve trailing-window gross trade volume per account, keyed by account_number.
 * NOT preloaded at login — fetched only when the Portfolios tab is sorted by
 * recent trade volume, then memoized for the session. Returns a Map for O(1)
 * lookups; resolves to an empty Map on failure so the caller degrades to
 * market-value ordering.
 */
export function loadTradeVolume() {
  if (volumeCache) return Promise.resolve(volumeCache);
  if (volumeInflight) return volumeInflight;
  volumeInflight = fetch(API_ENDPOINTS.reports.portfolioTradeVolume, { headers: authHeader() })
    .then((res) => {
      if (!res.ok) throw new Error(`trade-volume ${res.status}`);
      return res.json();
    })
    .then((data) => {
      volumeCache = new Map(Object.entries(data.volume_by_account || {}));
      volumeInflight = null;
      return volumeCache;
    })
    .catch((err) => {
      volumeInflight = null;
      throw err;
    });
  return volumeInflight;
}

// resolved Map(account_number -> { mv, prev_mv, net_flow, position_change,
// as_of, prev_as_of }) | null
let mvChangeCache = null;
let mvChangeInflight = null;

/**
 * Resolve per-account latest + prior performance-day market value, net client
 * flow and position change, keyed by account_number. Fetched fresh by the
 * Portfolios tab (market value is the default view) so it shows real values and
 * a day-over-day change even when the login-preload market-value aggregate
 * timed out (cold lake -> zeros). Returns a Map for O(1) lookups; rejects on
 * failure - callers catch and degrade to the preload MV (no change line).
 */
export function loadMarketValueChange() {
  if (mvChangeCache) return Promise.resolve(mvChangeCache);
  if (mvChangeInflight) return mvChangeInflight;
  mvChangeInflight = fetch(API_ENDPOINTS.reports.portfolioMarketValues, { headers: authHeader() })
    .then((res) => {
      if (!res.ok) throw new Error(`market-values ${res.status}`);
      return res.json();
    })
    .then((data) => {
      mvChangeCache = new Map(Object.entries(data.market_values || {}));
      mvChangeInflight = null;
      return mvChangeCache;
    })
    .catch((err) => {
      mvChangeInflight = null;
      throw err;
    });
  return mvChangeInflight;
}

/** Drop the cached options + any un-consumed preload key (call on logout / token clear). */
export function clearPortfolioOptions() {
  cache = null;
  inflight = null;
  volumeCache = null;
  volumeInflight = null;
  mvChangeCache = null;
  mvChangeInflight = null;
  try {
    sessionStorage.removeItem(STORAGE_KEYS.CODENAMES_PRELOAD);
  } catch (_) {
    // sessionStorage unavailable - nothing to clear.
  }
}
